import { useMutation, useQuery } from 'react-query';
import {
	createDraft,
	deleteDraft,
	getDraftById,
	getDrafts,
	getLatestDraft,
	updateDraft,
} from '@/services/drafts';
import type {
	CreateDraftPayload,
	Draft,
	UpdateDraftPayload,
} from '@/types/draft';

/**
 * Get all drafts for a user with optional filters
 */
export const useGetDrafts = ({
	userId,
	page = 1,
	limit = 15,
	search = '',
	columnFilters = [],
	enabled = true,
}: {
	userId: string;
	page?: number;
	limit?: number;
	search?: string;
	columnFilters?: any[];
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['drafts', userId, page, limit, search, JSON.stringify(columnFilters)],
		queryFn: () => getDrafts(userId, page, limit, search, columnFilters),
		enabled: enabled && !!userId,
		keepPreviousData: true,
	});
};

/**
 * Get the most recent draft for a specific form type
 */
export const useGetLatestDraft = ({
	userId,
	formType,
	enabled = true,
}: {
	userId: string;
	formType: string;
	enabled?: boolean;
}) => {
	return useQuery({
		queryKey: ['drafts', 'latest', userId, formType],
		queryFn: () => getLatestDraft(userId, formType),
		enabled: enabled && !!userId && !!formType,
		staleTime: 0, // Always fetch fresh data
		cacheTime: 0, // Don't cache
	});
};

/**
 * Get a single draft by ID
 */
export const useGetDraftById = (id: string | null, enabled = true) => {
	return useQuery({
		queryKey: ['drafts', id],
		queryFn: () => getDraftById(id as string),
		enabled: enabled && !!id,
	});
};

/**
 * Create a new draft
 */
export const useCreateDraft = (onSuccess?: (data: any) => void) => {
	return useMutation({
		mutationFn: (payload: CreateDraftPayload) => createDraft(payload),
		onSuccess,
	});
};

/**
 * Update an existing draft
 */
export const useUpdateDraft = (onSuccess?: (data: any) => void) => {
	return useMutation({
		mutationFn: ({
			id,
			payload,
		}: {
			id: string;
			payload: UpdateDraftPayload;
		}) => updateDraft(id, payload),
		onSuccess,
	});
};

/**
 * Create a new draft in create flows, or update the current draft in draft-edit flows.
 */
export const useSaveDraft = ({
	existingDraft,
	onSuccess,
}: {
	existingDraft?: Draft;
	onSuccess?: (data: any) => void;
}) => {
	const createDraftMutation = useCreateDraft(onSuccess);
	const updateDraftMutation = useUpdateDraft(onSuccess);

	const mutate = (payload: CreateDraftPayload) => {
		if (existingDraft?._id) {
			updateDraftMutation.mutate({
				id: existingDraft._id,
				payload: {
					draftType: payload.draftType,
					titleOrReference: payload.titleOrReference,
					formData: payload.formData,
				},
			});
			return;
		}

		createDraftMutation.mutate(payload);
	};

	return {
		mutate,
		isLoading: createDraftMutation.isLoading || updateDraftMutation.isLoading,
		isEditingDraft: !!existingDraft?._id,
	};
};

/**
 * Delete a draft
 */
export const useDeleteDraft = (onSuccess?: (data: any) => void) => {
	return useMutation({
		mutationFn: (id: string) => deleteDraft(id),
		onSuccess,
	});
};
